import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Button, Box, Typography } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { COLORS } from 'components';
import { CustomAccordionMenuProps } from './types';

export const CustomAccordionMenu = ({
  title,
  initialIcon,
  buttons,
  expanded = false,
  onChange,
  sx,
}: CustomAccordionMenuProps) => {
  const [internalExpanded, setInternalExpanded] = useState(expanded);
  const accordionRef = useRef<HTMLDivElement>(null);

  // Use external expanded state if onChange is provided, otherwise use internal state
  const isExpanded = onChange ? expanded : internalExpanded;

  // Handle click outside
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (accordionRef.current && !accordionRef.current.contains(event.target as Node)) {
      if (onChange) {
        onChange(false);
      } else {
        setInternalExpanded(false);
      }
    }
  }, [onChange]);

  useEffect(() => {
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, handleClickOutside]);

  const handleButtonClick = (onClick: () => void) => () => {
    onClick();
    if (onChange) {
      onChange(false);
    } else {
      setInternalExpanded(false);
    }
  };

  return (
    <Box ref={accordionRef} sx={{ position: 'relative', ...sx }}>
      <Accordion
        expanded={isExpanded}
        onChange={(e, expandedValue) => {
          if (onChange) {
            onChange(expandedValue);
          } else {
            setInternalExpanded(expandedValue);
          }
        }}
        sx={{
          boxShadow: 'none',
          backgroundColor: 'transparent',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0, boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }
        }}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white', fontSize: '1.5rem', transform: 'rotate(0deg)', transition: 'transform 0.3s ease' }} />}
          sx={{
            backgroundColor: COLORS.Mainblue,
            borderRadius: 2,
            minHeight: '36px',
            '& .MuiAccordionSummary-content': {
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              padding: 0,
              margin: 0
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {initialIcon && initialIcon}
            <Typography variant="body2" color="white" sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            p: 0.5,
            marginTop: '4px',
            border: '1px solid #C1C1C1',
            borderRadius: '8px',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            zIndex: 1000,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
          }}
        >
          {buttons.map((button, index) => (
            <Button
              key={button.label}
              variant="contained"
              startIcon={button.icon}
              onClick={handleButtonClick(button.onClick)}
              disabled={button.disabled}
              fullWidth={button.fullWidth}
              sx={{ mt: index > 0 ? '4px' : 0 }}
            >
              {button.label}
            </Button>
          ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};